import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Loader2, Mic2, MicOff, RefreshCw, UserCircle, UserRound } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ConclaveChangeTableSheet } from '@/components/conclave/ConclaveChangeTableSheet';
import { ConclaveEventShell } from '@/components/conclave/ConclaveEventShell';
import { ConclaveCurrentSpeakerBanner } from '@/components/conclave/ConclaveCurrentSpeakerBanner';
import { ConclaveSpeakingClock } from '@/components/conclave/ConclaveSpeakingClock';
import {
  formatSpeakingClock,
  sessionRemainingSeconds,
  speakingClockTone,
  userRemainingSeconds,
} from '@/components/conclave/speaking-clock';
import {
  ConclaveLiveHelpButton,
  ConclaveLiveTutorialCoach,
} from '@/components/conclave/ConclaveLiveTutorial';
import {
  LIVE_SESSION_TUTORIAL_STEPS,
  tutorialStepRing,
  type LiveTutorialStepId,
} from '@/components/conclave/conclave-live-tutorial';
import {
  ConclaveTableMateCard,
  type ConclaveTableMate,
} from '@/components/conclave/ConclaveTableMateCard';
import { SendPlainLeadSheet } from '@/components/conclave/SendPlainLeadSheet';
import { MateGiveAskSheet } from '@/components/conclave/MateGiveAskSheet';
import { useUser } from '@/hooks/use-user';
import { type GiveAskItem } from '@/lib/give-ask-api';
import { SendLeadSheet } from '@/pages/gives-asks/send-lead-sheet';
import { conclaveApi, type ConclaveSpeakingState } from '@/lib/conclave-api';
import { conclaveSubPath } from '@/lib/conclave-paths';
import { connectConclaveWs } from '@/lib/conclave-ws';
import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { useBackGuard, type BackGuard } from '@/hooks/use-back-guard';
import { toast } from 'sonner';

type ConclaveSessionRow = { id: string; index: number; status: 'PLANNED' | 'ACTIVE' | 'COMPLETED' };

type ConclaveNote = { id: string; body: string; aboutRegistrationId: string };

type TableData = {
  session?: { id: string; index: number; status: string } | null;
  assignment?: { tableNumber: number } | null;
  mates?: ConclaveTableMate[];
  expired?: boolean;
  message?: string;
};

function sessionStatusLabel(status?: string) {
  if (status === 'ACTIVE') return 'Ongoing';
  if (status === 'COMPLETED') return 'Completed';
  if (status === 'PLANNED') return 'Yet to start';
  return 'Waiting';
}

function speakingError(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error && 'response' in error) {
    const data = (error as { response?: { data?: { error?: string } } }).response?.data;
    if (data?.error) return data.error;
  }
  return fallback;
}

export default function ConclaveLivePage() {
  const { eventId = '', communityId } = useParams();
  const profilePath = conclaveSubPath(eventId, 'profile', communityId);
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [mateList, setMateList] = useState<{
    mate: ConclaveTableMate;
    kind: 'give' | 'ask';
  } | null>(null);
  const [leadTarget, setLeadTarget] = useState<GiveAskItem | null>(null);
  const [leadOwner, setLeadOwner] = useState<{ name: string; image: string | null } | null>(null);
  const [leadMate, setLeadMate] = useState<ConclaveTableMate | null>(null);
  const [table, setTable] = useState<TableData | null>(null);
  const tableRef = useRef(table);
  const [sessions, setSessions] = useState<ConclaveSessionRow[]>([]);
  const [nextSeat, setNextSeat] = useState<{ index: number; tableNumber: number } | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const selectedSessionRef = useRef<string | null>(null);
  const [activeSpeaker, setActiveSpeaker] = useState<{
    userId?: string;
    isSelf?: boolean;
    name?: string;
    image?: string | null;
    intro?: { title?: string; body?: string };
  } | null>(null);
  const [speaking, setSpeaking] = useState<ConclaveSpeakingState | null>(null);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const clockOffsetRef = useRef(0);
  const speakingLoadSeq = useRef(0);
  const speakingActionSeq = useRef(0);
  const latestSpeakingAt = useRef(0);
  const [tutorialMode, setTutorialMode] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [noteMate, setNoteMate] = useState<ConclaveTableMate | null>(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [notes, setNotes] = useState<ConclaveNote[]>([]);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [changeTableOpen, setChangeTableOpen] = useState(false);
  const canChangeTable = table?.session?.status === 'ACTIVE' && !table.expired;
  const [changeTableLock, setChangeTableLock] = useState<string | null>(null);

  function handleTableChipClick() {
    if (canChangeTable) {
      setChangeTableLock(null);
      setChangeTableOpen(true);
      return;
    }
    setChangeTableLock(
      table?.expired
        ? 'Table changes are closed after the event data expiry date.'
        : table?.session?.status === 'COMPLETED'
          ? 'This session has finished, so tables can no longer be changed.'
          : 'You can change your table once the session starts.'
    );
    setChangeTableOpen(true);
  }
  const [seatingRevision, setSeatingRevision] = useState(0);
  const wsRef = useRef<ReturnType<typeof connectConclaveWs> | null>(null);
  const [discardNoteOpen, setDiscardNoteOpen] = useState(false);
  const noteBack = useRef<BackGuard | null>(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const leaveBack = useRef<BackGuard | null>(null);
  const leaveGo = useRef<((options?: { replace?: boolean }) => void) | null>(null);
  const sessionOngoing = sessions.some((s) => s.status === 'ACTIVE');
  const liveGuard = useBackGuard(sessionOngoing, (back) => {
    leaveBack.current = back;
    leaveGo.current = null;
    setLeaveOpen(true);
  });
  useBackGuard(noteMate !== null, (back) => {
    if (!noteText.trim()) {
      setNoteMate(null);
      setNoteText('');
      return;
    }
    noteBack.current = back;
    setDiscardNoteOpen(true);
  });
  useEffect(() => {
    if (!sessionOngoing) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [sessionOngoing]);

  const timersOn = Boolean(speaking?.sessionTimerMinutes || speaking?.userTimerMinutes);
  useEffect(() => {
    if (!timersOn) return;
    const id = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [timersOn]);

  function applySpeaking(state: ConclaveSpeakingState) {
    const serverNow = new Date(state.serverNow).getTime();
    if (!Number.isNaN(serverNow)) {
      if (serverNow <= latestSpeakingAt.current) return;
      latestSpeakingAt.current = serverNow;
      clockOffsetRef.current = serverNow - Date.now();
    }
    setClockNow(Date.now());
    setSpeaking(state);
    if (!('activeSpeakers' in state)) return;
    const tableNumber = tableRef.current?.assignment?.tableNumber ?? null;
    const current =
      tableNumber == null
        ? null
        : (state.activeSpeakers?.find((speaker) => speaker.tableNumber === tableNumber) ?? null);
    if (!current) {
      setActiveSpeaker(null);
      return;
    }
    setActiveSpeaker({
      userId: current.userId,
      isSelf: Boolean(user?.id && current.userId === user.id),
      name: current.name,
      image: current.image,
      intro: current.intro
        ? {
            title: current.intro.title ?? undefined,
            body: current.intro.body ?? undefined,
          }
        : undefined,
    });
  }

  async function loadSpeaking(sessionId?: string | null) {
    const seq = ++speakingLoadSeq.current;
    try {
      const res = await conclaveApi.speaking(eventId, sessionId ?? undefined);
      if (seq !== speakingLoadSeq.current) return;
      applySpeaking(res.state);
    } catch {
      return;
    }
  }

  function bankedSpeaker(
    speaker: ConclaveSpeakingState['speakers'][number]
  ): ConclaveSpeakingState['speakers'][number] {
    if (!speaker.activeStartedAt) return speaker;
    const started = new Date(speaker.activeStartedAt).getTime();
    const live = Number.isNaN(started)
      ? 0
      : Math.max(0, Date.now() + clockOffsetRef.current - started);
    return { ...speaker, elapsedMs: speaker.elapsedMs + live, activeStartedAt: null };
  }

  function markSpeakerActive(userId: string) {
    setSpeaking((current) => {
      if (!current) return current;
      const existing = current.speakers.find((speaker) => speaker.userId === userId);
      const others = current.speakers
        .filter((speaker) => speaker.userId !== userId)
        .map((speaker) => bankedSpeaker(speaker));
      return {
        ...current,
        speakers: [
          ...others,
          {
            userId,
            tableNumber: existing?.tableNumber ?? tableRef.current?.assignment?.tableNumber ?? null,
            elapsedMs: existing?.elapsedMs ?? 0,
            activeStartedAt:
              existing?.activeStartedAt ??
              new Date(Date.now() + clockOffsetRef.current).toISOString(),
          },
        ],
      };
    });
  }

  function pauseSpeaker(userId: string) {
    setSpeaking((current) => {
      if (!current) return current;
      return {
        ...current,
        speakers: current.speakers.map((speaker) =>
          speaker.userId === userId ? bankedSpeaker(speaker) : speaker
        ),
      };
    });
  }

  const tutorialStepId = LIVE_SESSION_TUTORIAL_STEPS[tutorialStep]?.id;

  function stepActive(id: LiveTutorialStepId) {
    return tutorialMode && tutorialStepId === id;
  }

  function toggleTutorial() {
    setTutorialMode((on) => {
      if (on) return false;
      setTutorialStep(0);
      return true;
    });
  }

  async function refresh() {
    const sessionsRes = await conclaveApi.listSessions(eventId);
    const list = (sessionsRes.sessions ?? []) as ConclaveSessionRow[];
    const current = selectedSessionRef.current;
    const ongoing = list.find((s) => s.status === 'ACTIVE')?.id ?? null;
    const target =
      ongoing ??
      (current && list.some((s) => s.id === current) ? current : null) ??
      list[0]?.id ??
      null;
    selectedSessionRef.current = target;
    setSessions(list);
    setSelectedSessionId(target);
    const [tableRes, notesRes] = await Promise.all([
      conclaveApi.myTable(eventId, target ?? undefined),
      conclaveApi.listNotes(eventId),
    ]);
    tableRef.current = tableRes;
    setTable(tableRes);
    setNotes((notesRes.notes as ConclaveNote[]) || []);
    setNextSeat(await loadNextSeat(list, tableRes));
    await loadSpeaking(target);
  }

  async function loadNextSeat(
    list: ConclaveSessionRow[],
    tableRes: TableData
  ): Promise<{ index: number; tableNumber: number } | null> {
    const running = list.find((s) => s.status === 'ACTIVE');
    if (!running || tableRes.session?.id !== running.id || tableRes.assignment) return null;
    const upcoming = list.find((s) => s.status === 'PLANNED' && s.index > running.index);
    if (!upcoming) return null;
    try {
      const res = await conclaveApi.myTable(eventId, upcoming.id);
      if (res.assignment?.tableNumber == null) return null;
      return { index: upcoming.index, tableNumber: res.assignment.tableNumber };
    } catch {
      return null;
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refresh();
      } catch {
        if (!cancelled) toast.error('Failed to load live session');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  function handleReload() {
    window.location.reload();
  }

  function selectSession(sessionId: string) {
    selectedSessionRef.current = sessionId;
    setSelectedSessionId(sessionId);
    void refresh();
  }

  useEffect(() => {
    tableRef.current = table;
  }, [table]);

  useEffect(() => {
    const conn = connectConclaveWs(eventId, (msg) => {
      if (msg.type === 'connection.restored') {
        const current = tableRef.current;
        if (current?.session?.id && current.assignment?.tableNumber != null) {
          conn.send({
            type: 'join',
            sessionId: current.session.id,
            tableNumber: current.assignment.tableNumber,
          });
        }
        setSeatingRevision((n) => n + 1);
        void refresh();
        return;
      }
      if (msg.type === 'connection.replaced') {
        toast.error('Live updates paused: this session was opened in another tab.');
        return;
      }
      if (msg.type === 'speaking.timer') {
        const state = msg.state as ConclaveSpeakingState | undefined;
        if (state && Array.isArray(state.speakers) && typeof state.serverNow === 'string') {
          speakingLoadSeq.current += 1;
          applySpeaking(state);
        }
      }
      if (msg.type === 'speaking.started') {
        speakingLoadSeq.current += 1;
        const speakerUserId = typeof msg.userId === 'string' ? msg.userId : undefined;
        const intro =
          msg.intro && typeof msg.intro === 'object'
            ? (msg.intro as { title?: string; body?: string })
            : undefined;
        setActiveSpeaker((current) => ({
          userId: speakerUserId,
          isSelf: Boolean(speakerUserId && user?.id && speakerUserId === user.id),
          name:
            typeof msg.name === 'string'
              ? msg.name
              : current?.userId === speakerUserId
                ? current?.name
                : undefined,
          image:
            typeof msg.image === 'string'
              ? msg.image
              : current?.userId === speakerUserId
                ? current?.image
                : null,
          intro: intro ?? (current?.userId === speakerUserId ? current?.intro : undefined),
        }));
        if (speakerUserId) markSpeakerActive(speakerUserId);
        if (speakerUserId && speakerUserId !== user?.id) {
          void loadSpeaking(selectedSessionRef.current);
        }
      }
      if (msg.type === 'speaking.stopped') {
        speakingLoadSeq.current += 1;
        const stoppedUserId = typeof msg.userId === 'string' ? msg.userId : undefined;
        if (stoppedUserId) pauseSpeaker(stoppedUserId);
        setActiveSpeaker((current) =>
          !stoppedUserId || current?.userId === stoppedUserId ? null : current
        );
        void loadSpeaking(selectedSessionRef.current);
      }
      if (msg.type === 'session.completed' || msg.type === 'session.reset') {
        setActiveSpeaker(null);
        void refresh();
        return;
      }
      if (msg.type === 'assignment.updated') {
        setSeatingRevision((n) => n + 1);
        void refresh();
        return;
      }
      if (msg.type === 'session.started' || msg.type === 'config.updated') {
        void refresh();
      }
    });
    wsRef.current = conn;
    return () => {
      wsRef.current = null;
      conn.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, user?.id]);

  useEffect(() => {
    const conn = wsRef.current;
    const current = tableRef.current;
    if (conn && current?.session?.id && current.assignment?.tableNumber != null) {
      conn.send({
        type: 'join',
        sessionId: current.session.id,
        tableNumber: current.assignment.tableNumber,
      });
    }
  }, [table?.session?.id, table?.assignment?.tableNumber]);

  useEffect(() => {
    if (!tutorialMode || !tutorialStepId) return;
    const el = document.getElementById(`live-tutorial-${tutorialStepId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [tutorialMode, tutorialStep, tutorialStepId]);

  const mates = useMemo(() => table?.mates ?? [], [table?.mates]);
  const selfMate = useMemo(() => mates.find((m) => m.isSelf), [mates]);
  const tableMates = useMemo(() => mates.filter((m) => !m.isSelf), [mates]);
  const ongoingSession = sessions.find((s) => s.status === 'ACTIVE') ?? null;
  const stripMates = selfMate ? [selfMate, ...tableMates] : tableMates;
  const myTableNumber = table?.assignment?.tableNumber ?? null;
  const liveTurn =
    speaking?.speakers.find(
      (speaker) =>
        speaker.activeStartedAt && myTableNumber != null && speaker.tableNumber === myTableNumber
    ) ?? null;
  const floorUserId = liveTurn?.userId ?? activeSpeaker?.userId;
  const floorIsSelf = Boolean(floorUserId && user?.id && floorUserId === user.id);
  const speakerMate = floorUserId
    ? (mates.find((mate) => mate.userId === floorUserId) ??
      (floorIsSelf ? (selfMate ?? null) : null))
    : null;
  const namedActiveSpeaker = activeSpeaker?.userId === floorUserId ? activeSpeaker : null;
  const speakerImage = speakerMate?.image ?? namedActiveSpeaker?.image ?? null;
  const mateNotes = noteMate
    ? notes.filter((n) => n.aboutRegistrationId === noteMate.registrationId)
    : [];

  function openNote(mate: ConclaveTableMate) {
    setHighlightId(mate.registrationId);
    setNoteText('');
    setNoteMate(mate);
  }

  function closeNote(open: boolean) {
    if (!open) {
      setNoteMate(null);
      setNoteText('');
    }
  }

  function speakingIntro(intro?: { title?: string | null; body?: string | null } | null) {
    if (!intro?.title && !intro?.body) return undefined;
    return {
      title: intro.title ?? undefined,
      body: intro.body ?? undefined,
    };
  }

  async function startSpeaking() {
    const action = ++speakingActionSeq.current;
    speakingLoadSeq.current += 1;
    const intro = speakingIntro(selfMate?.intro);
    if (user?.id) markSpeakerActive(user.id);
    setActiveSpeaker({
      userId: user?.id,
      isSelf: true,
      name: selfMate?.name,
      image: selfMate?.image ?? null,
      intro,
    });
    try {
      const res = await conclaveApi.startSpeaking(eventId);
      if (action !== speakingActionSeq.current) return;
      applySpeaking(res.state);
      wsRef.current?.send({ type: 'speaking.start' });
    } catch (err) {
      if (action !== speakingActionSeq.current) return;
      void loadSpeaking(table?.session?.id ?? selectedSessionId);
      toast.error(speakingError(err, 'Could not sync the speaking timer'));
    }
  }

  async function stopSpeaking() {
    const action = ++speakingActionSeq.current;
    speakingLoadSeq.current += 1;
    if (user?.id) pauseSpeaker(user.id);
    setActiveSpeaker(null);
    try {
      const res = await conclaveApi.stopSpeaking(eventId);
      wsRef.current?.send({ type: 'speaking.stop' });
      if (action !== speakingActionSeq.current) return;
      applySpeaking(res.state);
    } catch {
      if (action !== speakingActionSeq.current) return;
      toast.error('Could not sync the speaking timer');
      void loadSpeaking(table?.session?.id ?? selectedSessionId);
    }
  }

  async function handleTableMoved() {
    setChangeTableOpen(false);
    await refresh();
  }

  async function submitNote(e: FormEvent) {
    e.preventDefault();
    if (!noteMate || !noteText.trim()) return;
    setSavingNote(true);
    try {
      await conclaveApi.createNote(eventId, {
        aboutRegistrationId: noteMate.registrationId,
        body: noteText.trim(),
      });
      setNoteText('');
      await refresh();
      toast.success('Note saved');
    } catch {
      toast.error('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const sessionIndex = (table?.session?.index ?? 0) + 1;
  const tableNumber = table?.assignment?.tableNumber;
  const clockOffset = clockOffsetRef.current;
  const sessionTotalSeconds = (speaking?.sessionTimerMinutes ?? 0) * 60;
  const userTotalSeconds = (speaking?.userTimerMinutes ?? 0) * 60;
  const sessionSeconds =
    table?.session?.status === 'ACTIVE'
      ? sessionRemainingSeconds(speaking, clockNow, clockOffset)
      : null;
  const speakerUserId = floorUserId;
  const speakerSeconds = floorUserId
    ? userRemainingSeconds(speaking, speakerUserId, clockNow, clockOffset)
    : null;
  const speakingClock = (
    <ConclaveSpeakingClock
      sessionSeconds={null}
      userSeconds={speakerSeconds}
      userTotalSeconds={userTotalSeconds}
    />
  );

  return (
    <ConclaveEventShell
      title="Live session"
      beforeLeave={(go) => {
        if (!sessionOngoing) {
          go();
          return;
        }
        leaveBack.current = null;
        leaveGo.current = go;
        setLeaveOpen(true);
      }}
    >
      <div className="mt-1 flex items-start justify-between gap-2 md:mt-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Collab round
          </p>
          <p className="text-lg font-semibold">
            Session {sessionIndex}
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              · {sessionStatusLabel(table?.session?.status)}
            </span>
          </p>
          {sessionSeconds != null ? (
            <div className="mt-2">
              <ConclaveSpeakingClock
                sessionSeconds={sessionSeconds}
                sessionTotalSeconds={sessionTotalSeconds}
                userSeconds={null}
              />
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 rounded-xl"
            onClick={handleReload}
            aria-label="Reload live session"
            title="Reload"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <ConclaveLiveHelpButton tutorialMode={tutorialMode} onToggle={toggleTutorial} />
          <Button
            asChild
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 rounded-xl"
            aria-label="Edit networking profile"
            title="Edit networking profile"
          >
            <Link to={profilePath} state={{ returnToLive: true }}>
              <UserCircle className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {tutorialMode ? (
          <ConclaveLiveTutorialCoach
            key="live-tutorial-coach"
            stepIndex={tutorialStep}
            onStepIndexChange={setTutorialStep}
            onClose={() => setTutorialMode(false)}
          />
        ) : null}
      </AnimatePresence>

      {table?.expired ? (
        <p className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-900">
          {table.message || 'Peer data hidden after expiry.'}
        </p>
      ) : null}

      <div className={tutorialStepRing(stepActive('table'), 'mt-5')}>
        {ongoingSession ? (
          <>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Tablemates
            </p>
            {tableMates.length === 0 ? (
              <p className="text-sm text-muted-foreground">No other attendees at this table yet.</p>
            ) : (
              <div className="flex snap-x gap-3 overflow-x-auto pb-2">
                {stripMates.map((mate) => {
                  const isSpeaking =
                    Boolean(floorUserId && mate.userId && mate.userId === floorUserId) ||
                    Boolean(floorIsSelf && mate.isSelf);
                  const mateUserId = mate.userId ?? (mate.isSelf ? user?.id : undefined);
                  const mateUsesUserTimer = Boolean(speaking?.userTimerMinutes);
                  const mateSeconds = isSpeaking
                    ? mateUsesUserTimer
                      ? userRemainingSeconds(speaking, mateUserId, clockNow, clockOffset)
                      : sessionSeconds
                    : null;
                  const mateTotalSeconds = mateUsesUserTimer
                    ? userTotalSeconds
                    : sessionTotalSeconds;
                  const mateTone =
                    mateSeconds == null ? 'ok' : speakingClockTone(mateSeconds, mateTotalSeconds);
                  return (
                    <button
                      key={mate.registrationId}
                      type="button"
                      onClick={() =>
                        document
                          .getElementById(`mate-${mate.registrationId}`)
                          ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                      }
                      className={`flex w-36 shrink-0 snap-start flex-col items-center gap-2 rounded-2xl border px-3 py-3 text-center shadow-sm transition ${
                        isSpeaking
                          ? 'border-primary/50 bg-primary/5 shadow-primary/15'
                          : 'border-border bg-card'
                      }`}
                    >
                      <span className="relative flex h-14 w-14 items-center justify-center">
                        {isSpeaking ? (
                          <>
                            <span
                              aria-hidden
                              className="animate-speaking-ring absolute inset-0 rounded-full border-2 border-primary"
                            />
                            <span
                              aria-hidden
                              className="animate-speaking-ring-delayed absolute inset-0 rounded-full border-2 border-primary/70"
                            />
                          </>
                        ) : null}
                        <Avatar
                          className={`relative z-10 h-14 w-14 rounded-full border-2 ${
                            isSpeaking
                              ? 'border-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.25)]'
                              : 'border-primary/40 ring-2 ring-primary/15'
                          }`}
                        >
                          <AvatarImage src={mate.image ?? undefined} alt={mate.name} />
                          <AvatarFallback>
                            <UserRound className="h-6 w-6" />
                          </AvatarFallback>
                        </Avatar>
                      </span>
                      <span className="w-full truncate text-sm font-semibold">
                        {mate.isSelf ? 'You' : mate.name}
                      </span>
                      {mateSeconds != null ? (
                        <span
                          className={`text-xs font-semibold tabular-nums ${
                            mateTone === 'overtime'
                              ? 'text-red-600'
                              : mateTone === 'warning'
                                ? 'text-yellow-700'
                                : 'text-primary'
                          }`}
                        >
                          {formatSpeakingClock(mateSeconds)}
                        </span>
                      ) : null}
                      <span className="text-xs text-muted-foreground">
                        {mate.giveAskItems?.gives.length ?? 0} gives ·{' '}
                        {mate.giveAskItems?.asks.length ?? 0} asks
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Sessions
            </p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {sessions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => selectSession(s.id)}
                  className={`shrink-0 rounded-xl border px-3 py-2 text-left text-sm ${
                    s.id === selectedSessionId
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-background text-foreground'
                  }`}
                >
                  <span className="block font-semibold">Session {s.index + 1}</span>
                  <span className="block text-xs text-muted-foreground">
                    {sessionStatusLabel(s.status)}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      {nextSeat ? (
        <p className="mt-3 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 text-sm text-foreground md:mt-4">
          You&apos;re not seated in this running session. You&apos;re assigned to table{' '}
          <span className="font-semibold">{nextSeat.tableNumber}</span> in Session{' '}
          {nextSeat.index + 1}.
        </p>
      ) : null}
      <div className="mt-3 md:mt-4">
        <div className="flex items-center justify-between gap-3">
          <div
            id="live-tutorial-speaking"
            className={tutorialStepRing(stepActive('speaking'), 'flex min-w-0 flex-1 items-center')}
          >
            {table?.session?.status !== 'ACTIVE' ? (
              <p className="text-sm leading-snug text-muted-foreground">
                Speaking is available during the ongoing session.
              </p>
            ) : floorIsSelf ? (
              <Button
                type="button"
                variant="destructive"
                className="rounded-xl"
                onClick={stopSpeaking}
              >
                <MicOff className="mr-2 h-4 w-4" />
                Stop speaking
              </Button>
            ) : (
              <Button type="button" className="rounded-xl" onClick={startSpeaking}>
                <Mic2 className="mr-2 h-4 w-4" />
                {floorUserId ? 'Take the floor' : 'Start speaking'}
              </Button>
            )}
          </div>
          <div
            id="live-tutorial-table"
            className={tutorialStepRing(stepActive('table'), 'shrink-0')}
          >
            <button
              type="button"
              onClick={handleTableChipClick}
              aria-label={canChangeTable ? 'Your table. Tap to change table' : 'Your table'}
              className="flex h-11 flex-col justify-center rounded-xl bg-primary px-4 text-center text-primary-foreground shadow-primary/20 shadow-md transition hover:opacity-90"
            >
              <span className="text-[10px] font-semibold uppercase leading-none tracking-wider opacity-90">
                Your table
              </span>
              <span className="mt-0.5 text-lg font-bold leading-none">{tableNumber ?? '—'}</span>
            </button>
          </div>
        </div>
      </div>

      <div id="live-tutorial-speaker" className={tutorialStepRing(stepActive('speaker'))}>
        {floorUserId ? (
          <ConclaveCurrentSpeakerBanner
            key={floorUserId}
            speakerName={namedActiveSpeaker?.name || speakerMate?.name}
            speakerImage={speakerImage}
            intro={namedActiveSpeaker?.intro ?? speakerMate?.intro}
            mate={speakerMate}
            speakingClock={speakingClock}
            onGive={(m) => setMateList({ mate: m, kind: 'give' })}
            onAsk={(m) => setMateList({ mate: m, kind: 'ask' })}
            onNote={openNote}
            onLead={(m) => setLeadMate(m)}
          />
        ) : stepActive('speaker') ? (
          <div className="mt-4 rounded-xl border border-dashed border-primary/40 bg-primary/5 px-3 py-4 text-center text-sm text-muted-foreground">
            When someone taps Start speaking, their name, photo, intro and profile actions appear
            here for the whole table.
          </div>
        ) : null}
      </div>

      <div
        id="live-tutorial-mates"
        className={tutorialStepRing(
          stepActive('mates') || stepActive('actions'),
          'mt-4 space-y-2 md:mt-6 md:space-y-3'
        )}
      >
        <div id="live-tutorial-actions" className="sr-only" aria-hidden />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Table {tableNumber ?? '—'}
        </h2>
        {tableMates.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              No other attendees at this table yet.
            </CardContent>
          </Card>
        ) : (
          tableMates.map((mate) => (
            <div key={mate.registrationId} id={`mate-${mate.registrationId}`}>
              <ConclaveTableMateCard
                mate={mate}
                onGive={(m) => setMateList({ mate: m, kind: 'give' })}
                onAsk={(m) => setMateList({ mate: m, kind: 'ask' })}
                onNote={openNote}
                onLead={(m) => setLeadMate(m)}
                className={
                  highlightId === mate.registrationId ? 'ring-2 ring-primary/30' : undefined
                }
              />
            </div>
          ))
        )}

        {selfMate ? (
          <div id={`mate-${selfMate.registrationId}`}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              You
            </p>
            <ConclaveTableMateCard mate={selfMate} />
          </div>
        ) : null}
      </div>

      <SendPlainLeadSheet
        eventId={eventId}
        mate={leadMate}
        onOpenChange={(open) => {
          if (!open) setLeadMate(null);
        }}
      />

      {table?.session?.id ? (
        <ConclaveChangeTableSheet
          open={changeTableOpen}
          onOpenChange={setChangeTableOpen}
          eventId={eventId}
          sessionId={table?.session?.id ?? null}
          revision={seatingRevision}
          lockedMessage={changeTableLock}
          onMoved={() => {
            void handleTableMoved();
          }}
        />
      ) : null}

      <MateGiveAskSheet
        mate={mateList?.mate ?? null}
        kind={mateList?.kind ?? 'give'}
        onOpenChange={(open) => {
          if (!open) setMateList(null);
        }}
        onReply={(item) => {
          setLeadOwner(
            mateList ? { name: mateList.mate.name, image: mateList.mate.image ?? null } : null
          );
          setLeadTarget(item);
          setMateList(null);
        }}
      />

      <SendLeadSheet
        communityId={null}
        eventId={eventId}
        target={leadTarget}
        owner={leadOwner}
        onOpenChange={(open) => {
          if (!open) setLeadTarget(null);
        }}
        onSent={() => setLeadTarget(null)}
      />

      <Sheet
        open={noteMate !== null}
        onOpenChange={(open) => {
          if (open || noteMate === null) return;
          if (noteText.trim()) {
            setDiscardNoteOpen(true);
            return;
          }
          closeNote(false);
        }}
      >
        <SheetContent
          side="bottom"
          className="mx-auto max-h-[85dvh] w-full max-w-lg overflow-y-auto rounded-t-2xl border-t px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-6 sm:px-6"
        >
          {noteMate ? (
            <>
              <SheetHeader className="text-left">
                <SheetTitle>Note about {noteMate.name}</SheetTitle>
                <SheetDescription>Private to you — they will not see this.</SheetDescription>
              </SheetHeader>
              <form onSubmit={submitNote} className="mt-5 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="note-text">Note</Label>
                  <Textarea
                    id="note-text"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    required
                    autoFocus
                    className="min-h-28 rounded-xl"
                    placeholder="Your private note…"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full rounded-xl"
                  disabled={savingNote || !noteText.trim()}
                >
                  {savingNote ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Save note
                </Button>
              </form>
              {mateNotes.length > 0 ? (
                <ul className="mt-5 space-y-2 border-t border-border pt-4 text-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Saved notes
                  </p>
                  {mateNotes.map((n) => (
                    <li key={n.id} className="rounded-xl bg-muted/60 px-3 py-2">
                      {n.body}
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : null}
        </SheetContent>
      </Sheet>
      <ConfirmDialog
        open={discardNoteOpen}
        title="Discard this note?"
        description="What you typed will be lost."
        confirmLabel="Discard"
        destructive
        onCancel={() => {
          noteBack.current?.stay();
          noteBack.current = null;
          setDiscardNoteOpen(false);
        }}
        onConfirm={() => {
          noteBack.current = null;
          setDiscardNoteOpen(false);
          closeNote(false);
        }}
      />
      <ConfirmDialog
        open={leaveOpen}
        title="Leave the live session?"
        description="A session is ongoing. You can come back to Live later, but you may miss table updates while you're away."
        confirmLabel="Leave"
        cancelLabel="Stay"
        destructive
        onCancel={() => {
          leaveBack.current?.stay();
          leaveBack.current = null;
          leaveGo.current = null;
          setLeaveOpen(false);
        }}
        onConfirm={() => {
          setLeaveOpen(false);
          const go = leaveGo.current;
          leaveGo.current = null;
          if (go) {
            liveGuard.drop();
            go({ replace: true });
            return;
          }
          const back = leaveBack.current;
          leaveBack.current = null;
          back?.leave();
        }}
      />
    </ConclaveEventShell>
  );
}
